// The transfer status stack's rules (#6773, epic #6552; prototype mvdHost through mvdFail,
// `docs/design/fleet-redesign/prototype.html:3101-3244`). Pure: every function here takes its
// clock (`now`) and its reads as arguments. `./transfer-stack-store.ts` holds the cards and their
// timers; `components/TransferStack.tsx` renders them into the slot above the floating Core.
//
// A CARD IS ONE TRANSFER: a move this page sent and the server accepted, or one it refused.
// Cards are ordered by when their send or drag STARTED (`orderStamp`), never by when the answer
// arrived, and at most 24 are kept (the oldest goes first, whatever its state).
//
// HELD OR FAILED IS THE REFUSAL'S REASON (`MOVE_WAIT_REASONS`, the four 429s), read off the
// refusal OBJECT, never the prototype's text regex (HELDRE, `:3088`) or a translated sentence.
//
// STEPS FOLLOW THE SERVER. An accepted card's state is a pure function of ITS OWN accepted ref
// and each task's newest fresh read (`judgeMoveRead`): a read that started before the 201 counts
// for nothing, and a task a later batch moved reads `superseded`, which retires the card. The
// prototype's step times (mvdRun, `:3228-3236`) are MINIMUMS from the card's arrival: a step
// shows only once both its time has come and a fresh read reached it. "Move complete" needs a
// fresh read showing every item landed AND started (`in_progress`) or gone from the machine
// (`held === false`); a landed item that never starts stays at Step 3, with no timer.

import type { Translator } from '@developerz/i18n';
import { capabilityKey } from './machine-jobs';
import { MOVE_WAIT_REASONS, type MoveRefusalWire } from './move-calls';
import type { MoveSubject } from './move-items';
import { type AcceptedMoveRef, judgeMoveRead, type MoveRead } from './move-read';

/** At most this many cards are kept; the oldest goes first. */
export const TRANSFER_CAP = 24;

/** When each step may show, from the card's arrival: Transfer, Resume, Move complete. */
export const STEP_TIMES_MS: readonly [number, number, number] = [420, 1_050, 1_750];
/** The same under reduced motion. */
export const STEP_TIMES_REDUCED_MS: readonly [number, number, number] = [120, 300, 520];

/** How long a settled card stays before it clears itself, from its settling. */
export const CLEAR_MS = { complete: 5_000, putBack: 3_800, failed: 9_000, held: 9_000 } as const;

/** On release (the stack closed), each self-clearing card clears `base + step × index` from then. */
export const RELEASE_MS = { base: 2_400, step: 300 } as const;

/** Where a card came from. */
export type TransferOrigin =
  /** A 201: followed through its tasks' fresh reads, judged by this ref. */
  | {
      readonly kind: 'accepted';
      readonly ref: AcceptedMoveRef;
      readonly taskIds: readonly string[];
    }
  /** A refusal: its object (null for a 404, a 5xx or the network). */
  | { readonly kind: 'refused'; readonly refusal: MoveRefusalWire | null };

export type TransferCard = {
  /** The stack's own id for the card. */
  readonly id: number;
  /** When its send or drag started (`orderStamp`): the newest card is the highest. */
  readonly order: number;
  /** When the card arrived, `Date.now` ms: the step minimums count from it. */
  readonly arrivedAt: number;
  readonly name: string;
  readonly fromId: string;
  /** Null for a drag refused at its source: it had no target. */
  readonly toId: string | null;
  /** An Undo or a Put-back part: "Putting back" / "Put back", cleared after 3.8 s. */
  readonly putBack: boolean;
  readonly origin: TransferOrigin;
};

export type TransferState =
  | { readonly kind: 'moving'; readonly step: 0 | 1 | 2 }
  /** Every item landed and started (or left the machine); `running`: every one `in_progress`. */
  | { readonly kind: 'complete'; readonly running: boolean }
  | { readonly kind: 'held'; readonly refusal: MoveRefusalWire }
  /** `batch` names how many of a lifecycle failure landed; a refusal carries none. */
  | {
      readonly kind: 'failed';
      readonly refusal: MoveRefusalWire | null;
      readonly batch: { readonly moved: number; readonly total: number } | null;
    }
  /** A later batch moved one of its tasks: the card leaves at once. */
  | { readonly kind: 'retired' };

/** A wait (one of the four 429s): a held card. Any other refusal, or none, is a failed one. */
export function refusalHeld(r: MoveRefusalWire | null): r is MoveRefusalWire {
  return r !== null && MOVE_WAIT_REASONS.has(r.reason);
}

/** What the server's lifecycle says of an accepted batch, before the step minimums. */
export type TransferLifecycle =
  | { readonly kind: 'step'; readonly step: 0 | 1 | 2 | 3; readonly running: boolean }
  | { readonly kind: 'failed'; readonly moved: number; readonly total: number }
  | { readonly kind: 'retired' };

type ItemStep = 0 | 1 | 2 | 3 | 'failed';

/** One task's step on its newest fresh read, judged with the card's own ref. */
function itemStep(read: MoveRead | undefined, ref: AcceptedMoveRef): ItemStep | 'superseded' {
  const j = judgeMoveRead(read, ref);
  if (j.kind === 'superseded') return 'superseded';
  if (j.kind === 'pending') return 0;
  if (j.move.state === 'failed') return 'failed';
  if (j.move.state === 'moving') return 1;
  if (j.move.state !== 'landed') return 0;
  return read?.status === 'in_progress' || read?.held === false ? 3 : 2;
}

/**
 * The batch's lifecycle on its tasks' fresh reads: retired once any reads a later batch; failed
 * once every item settled (landed or failed) and any failed; else its SLOWEST item's step.
 */
export function transferLifecycle(
  taskIds: readonly string[],
  ref: AcceptedMoveRef,
  readOf: (taskId: string) => MoveRead | undefined,
): TransferLifecycle {
  const steps: ItemStep[] = [];
  for (const id of taskIds) {
    const s = itemStep(readOf(id), ref);
    if (s === 'superseded') return { kind: 'retired' };
    steps.push(s);
  }
  const failed = steps.filter((s) => s === 'failed').length;
  const moving = steps.filter((s): s is 0 | 1 | 2 | 3 => s !== 'failed');
  const landed = moving.filter((s) => s >= 2).length;
  if (failed > 0 && landed === moving.length) {
    return { kind: 'failed', moved: landed, total: taskIds.length };
  }
  const step = moving.length === 0 ? 0 : (Math.min(...moving) as 0 | 1 | 2 | 3);
  const running =
    step === 3 && taskIds.every((id) => readOf(id)?.status === 'in_progress') && failed === 0;
  return { kind: 'step', step, running };
}

/** How many steps the card's time allows at `now`: 0 at arrival, 3 once Move complete may show. */
function timeStep(arrivedAt: number, now: number, times: readonly number[]): number {
  return times.filter((ms) => now - arrivedAt >= ms).length;
}

/**
 * A card's state at `now`, and when its time next allows a step the server already reached
 * (null when nothing waits on the clock). Pure of the card and the reads.
 */
export function transferState(
  card: TransferCard,
  readOf: (taskId: string) => MoveRead | undefined,
  now: number,
  reduced: boolean,
): { readonly state: TransferState; readonly nextAt: number | null } {
  const o = card.origin;
  if (o.kind === 'refused') {
    const state: TransferState = refusalHeld(o.refusal)
      ? { kind: 'held', refusal: o.refusal }
      : { kind: 'failed', refusal: o.refusal, batch: null };
    return { state, nextAt: null };
  }
  const l = transferLifecycle(o.taskIds, o.ref, readOf);
  if (l.kind === 'retired') return { state: l, nextAt: null };
  if (l.kind === 'failed') {
    const batch = { moved: l.moved, total: l.total };
    return { state: { kind: 'failed', refusal: null, batch }, nextAt: null };
  }
  const times = reduced ? STEP_TIMES_REDUCED_MS : STEP_TIMES_MS;
  const allowed = timeStep(card.arrivedAt, now, times);
  const shown = Math.min(l.step, allowed);
  const nextAt = l.step > allowed ? card.arrivedAt + (times[allowed] ?? 0) : null;
  if (shown === 3) return { state: { kind: 'complete', running: l.running }, nextAt: null };
  return { state: { kind: 'moving', step: shown as 0 | 1 | 2 }, nextAt };
}

/** A settled card reads nothing more: its state is latched. Moving is not settled. */
export const transferSettled = (s: TransferState): boolean => s.kind !== 'moving';

/** Insert `card` by its start stamp (oldest first) and keep the newest 24. */
export function insertCard(
  cards: readonly TransferCard[],
  card: TransferCard,
): { readonly cards: readonly TransferCard[]; readonly dropped: readonly TransferCard[] } {
  const next = [...cards, card].sort((a, b) => a.order - b.order);
  const over = Math.max(0, next.length - TRANSFER_CAP);
  return { cards: next.slice(over), dropped: next.slice(0, over) };
}

/** What a card's self-clear reads besides its own state. */
export type ClearContext = {
  /** The stack is open (pinned, hovered or focused). */
  readonly open: boolean;
  /** When the stack last closed, `Date.now` ms; null while it never did. */
  readonly releasedAt: number | null;
  /** The card's index, 0 the OLDEST (as mvdRelease iterates its list). */
  readonly index: number;
  /** The card's Undo window is open (the note names its batch; resting, starting or live). */
  readonly undoOpen: boolean;
  /** When its Undo window ended or it lost the Undo; null while it never held one. */
  readonly undoEndedAt: number | null;
};

/** A held card's wait ends at its `free_at`, `Date.now` ms; null when it names none. */
export function heldFreeAt(r: MoveRefusalWire): number | null {
  if (r.free_at == null) return null;
  const at = Date.parse(r.free_at);
  return Number.isNaN(at) ? null : at;
}

/** When a settled card stopped waiting on anything but its clear time; null while it waits. */
function selfClearingSince(
  state: TransferState,
  settledAt: number,
  c: ClearContext,
): number | null {
  if (state.kind !== 'complete') return settledAt;
  if (c.undoOpen) return null;
  return Math.max(settledAt, c.undoEndedAt ?? settledAt);
}

/** How long after it starts clearing itself a card goes. */
function clearDelay(card: TransferCard, state: TransferState): number {
  if (state.kind === 'held') return CLEAR_MS.held;
  if (state.kind === 'failed') return CLEAR_MS.failed;
  return card.putBack ? CLEAR_MS.putBack : CLEAR_MS.complete;
}

/**
 * When a card clears itself, `Date.now` ms, or null while it does not. A moving card never
 * self-clears; a retired card leaves at once, open stack or not. Nothing clears while the stack is open, except a
 * held card whose wait passed. A card that was already clearing itself when the stack closed is
 * re-timed to 2.4 s + 0.3 s × its index from then; a complete card whose Undo window is open
 * neither clears nor is re-timed.
 */
export function clearAt(
  card: TransferCard,
  state: TransferState,
  settledAt: number,
  c: ClearContext,
): number | null {
  if (state.kind === 'moving') return null;
  if (state.kind === 'retired') return settledAt;
  const wait = state.kind === 'held' ? heldFreeAt(state.refusal) : null;
  const since = selfClearingSince(state, settledAt, c);
  let at: number | null = null;
  if (since !== null && !c.open) {
    at =
      c.releasedAt !== null && c.releasedAt >= since
        ? c.releasedAt + RELEASE_MS.base + RELEASE_MS.step * c.index
        : since + clearDelay(card, state);
  }
  if (wait === null) return at;
  return at === null ? wait : Math.min(at, wait);
}

export type TransferSummary = {
  readonly total: number;
  readonly moving: number;
  readonly done: number;
  readonly held: number;
  readonly failed: number;
  /** How many distinct machines the cards came from. */
  readonly machines: number;
};

/** The summary bar's counts. */
export function transferSummary(
  cards: readonly { readonly fromId: string; readonly state: TransferState }[],
): TransferSummary {
  const n = (k: TransferState['kind']): number => cards.filter((c) => c.state.kind === k).length;
  return {
    total: cards.length,
    moving: n('moving'),
    done: n('complete'),
    held: n('held'),
    failed: n('failed'),
    machines: new Set(cards.map((c) => c.fromId)).size,
  };
}

/** One card's place in the stack. */
export type CardPlace = {
  /** Upward offset from the stack's bottom, px. */
  readonly y: number;
  readonly scale: number;
  readonly opacity: number;
  /** A collapsed card behind the front one: no content, only its outline. */
  readonly behind: boolean;
  /** Out of view (too old to peek, or does not fit expanded): not interactive. */
  readonly hidden: boolean;
  /** A card behind the front one takes the front one's height, px; null at its own. */
  readonly height: number | null;
};

export type StackLayout = {
  /** Per card, in the order given (oldest first). */
  readonly places: readonly CardPlace[];
  /** The stack's own height, px. */
  readonly height: number;
  /** Cards not shown ("+N older"). */
  readonly older: number;
};

const PEEK_PX = 12;
const GAP_PX = 8;

/**
 * Where each card sits (mvdLayout, `:3168-3207`). `heights` are the cards' natural heights,
 * oldest first. Collapsed: the newest in front, up to two older peeking 12 px higher each,
 * scaled and faded. Expanded (`open`, two or more cards): fanned upward with 8 px gaps up to
 * `maxH`; what does not fit is hidden and counted.
 */
export function stackLayout(heights: readonly number[], open: boolean, maxH: number): StackLayout {
  const n = heights.length;
  const front = heights[n - 1] ?? 0;
  const places: CardPlace[] = new Array(n);
  let y = 0;
  let older = 0;
  for (let i = n - 1; i >= 0; i--) {
    const d = n - 1 - i;
    const h = heights[i] ?? 0;
    if (open) {
      const fits = d === 0 || y + h <= maxH;
      places[i] = {
        y,
        scale: 1,
        opacity: fits ? 1 : 0,
        behind: false,
        hidden: !fits,
        height: null,
      };
      if (fits) y += h + GAP_PX;
      else older += 1;
    } else {
      const peeks = d <= 2;
      places[i] = {
        y: d * PEEK_PX,
        scale: 1 - Math.min(d, 3) * 0.05,
        opacity: peeks ? 1 - d * 0.2 : 0,
        behind: d > 0,
        hidden: !peeks,
        height: d > 0 ? front : null,
      };
      if (!peeks) older += 1;
    }
  }
  const height = open ? Math.max(0, y - GAP_PX) : front + Math.min(Math.max(n - 1, 0), 2) * PEEK_PX;
  return { places, height, older };
}

/** The card's name for one item (trkName, `:3095-3100`); a batch's is its note's `what`. */
export function transferName(subject: MoveSubject, tr: Translator): string {
  if (subject.item.kind === 'epic') return tr('fleet.transfer.name_epic', { title: subject.title });
  const role = tr(capabilityKey(subject.jobs[0]?.role ?? null));
  if (subject.item.kind === 'group') {
    return tr('fleet.transfer.name_group', { role, jobs: subject.title });
  }
  return tr('fleet.transfer.name_job', { role, title: subject.title });
}
